import { Button, CircularProgress, Dialog } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { CmpProspectMemberMatchOptionReturn } from "../../../graphql/types";
import {
  InvoiceDesignScope,
  InvoiceDialogCloseButton,
  invoiceDialogPaperSx,
} from "../Invoicing/InvoiceDesignSystem";
import InvoiceSearchableSelect from "../Invoicing/InvoiceSearchableSelect";
import {
  prospectDialogActionSx,
  prospectDesignFontFamily,
  prospectDialogCancelSx,
  prospectDialogSx,
  prospectMatchDialogCloseIconOptions,
  prospectNoOptionsSx,
  prospectSelectMenuSx,
  prospectSelectSx,
} from "./prospectDesign";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
} from "../../../utils/userFacingError";

export type ProspectMatchMemberDialogProps = Readonly<{
  open: boolean;
  companyName: string;
  members: readonly CmpProspectMemberMatchOptionReturn[];
  loading: boolean;
  saving: boolean;
  error?: string;
  onClose: () => void;
  onMatch: (memberId: string) => Promise<void>;
}>;

export const ProspectMatchMemberDialog = ({
  open,
  companyName,
  members,
  loading,
  saving,
  error,
  onClose,
  onMatch,
}: ProspectMatchMemberDialogProps): React.ReactElement => {
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [selectionError, setSelectionError] = useState(false);
  const [requestError, setRequestError] = useState("");
  const sortedMembers = useMemo(
    () =>
      [...members].sort((left, right) =>
        left.name.localeCompare(right.name, "en-CA", {
          numeric: true,
          sensitivity: "base",
        }),
      ),
    [members],
  );
  const selectedMember =
    sortedMembers.find(({ id }) => id === selectedMemberId) || null;

  useEffect(() => {
    setSelectedMemberId("");
    setSelectionError(false);
    setRequestError("");
  }, [open, companyName]);

  const handleClose = () => {
    if (!saving) onClose();
  };

  const handleMatch = async () => {
    if (loading || saving) return;
    if (!selectedMember) {
      setSelectionError(true);
      return;
    }

    setRequestError("");
    try {
      await onMatch(selectedMember.id);
    } catch (matchError) {
      setRequestError(
        getUserFacingErrorText(
          matchError,
          buildStaffErrorFallback(
            `match ${companyName} to ${selectedMember.name}`,
          ),
        ),
      );
    }
  };

  return (
    <InvoiceDesignScope fontFamily={prospectDesignFontFamily}>
      <Dialog
        aria-labelledby="prospect-match-heading"
        aria-describedby="prospect-match-description"
        disableEscapeKeyDown={saving}
        maxWidth={false}
        onClose={handleClose}
        open={open}
        PaperProps={{ sx: invoiceDialogPaperSx({ maxWidth: 520 }) }}
        sx={prospectDialogSx}
      >
        <div className="relative overflow-y-auto">
          <InvoiceDialogCloseButton
            ariaLabel="Close"
            outlinedIcon={prospectMatchDialogCloseIconOptions}
            hoverBackgroundColor="#F21B1B"
            disabled={saving}
            onClick={handleClose}
          />
          <div className="px-[26px] pt-6">
            <h2
              id="prospect-match-heading"
              className="pr-4 text-[17px] font-semibold leading-[normal] text-[#0A0A0A] md:pr-0"
            >
              Match Prospect to Existing Member
            </h2>
            <p
              id="prospect-match-description"
              className="mt-1.5 break-words text-[14px] font-normal leading-[1.55] text-[#0A0A0A]"
            >
              Manually match <span className="font-semibold">{companyName}</span>
              {" to an existing member record. The prospect will be marked as Converted to Member and kept for conversion tracking."}
            </p>
          </div>
          <div className="flex flex-col gap-2 border-b border-[#ECECEC] px-[26px] pb-6 pt-5">
            <label
              id="prospect-match-member-label"
              htmlFor="prospect-match-member"
              className="text-[14px] font-semibold leading-[normal] text-[#0A0A0A]"
            >
              Select Member <span className="text-[#E24C4C]">*</span>
            </label>
            <InvoiceSearchableSelect
              id="prospect-match-member"
              labelledById="prospect-match-member-label"
              disabled={saving}
              loading={loading}
              error={selectionError}
              sx={{
                ...prospectSelectSx,
                ...(selectionError ? {
                  "& [data-invoice-select-trigger]": { borderColor: "#9E9E9E" },
                } : {}),
              }}
              getOptionKey={({ id }) => id}
              getOptionLabel={({ name }) => name}
              options={sortedMembers}
              optionRowHeight={40}
              outlinedArrow
              value={selectedMember}
              onChange={(member) => {
                setSelectedMemberId(member?.id || "");
                setSelectionError(false);
                setRequestError("");
              }}
              placeholder="Select Member Name"
              searchPlaceholder="Search Member Name"
              noOptionsText="No matches found"
              noOptionsSx={prospectNoOptionsSx}
              menuSx={prospectSelectMenuSx}
              required
            />
            {selectionError && (
              <p role="alert" className="text-[14px] leading-[normal] text-[#E24C4C]">
                Select a member to match this prospect to
              </p>
            )}
            {(error || requestError) && (
              <p role="alert" className="text-[14px] leading-[1.55] text-[#E24C4C]">
                {error || requestError}
              </p>
            )}
          </div>
          <div className="flex flex-wrap justify-end gap-3 px-[26px] py-[18px]">
            <Button
              disabled={saving}
              onClick={handleClose}
              sx={prospectDialogCancelSx}
              type="button"
            >
              Cancel
            </Button>
            <Button
              disabled={saving || loading}
              onClick={() => void handleMatch()}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={prospectDialogActionSx}
              type="button"
            >
              Match Member
            </Button>
          </div>
        </div>
      </Dialog>
    </InvoiceDesignScope>
  );
};

export default ProspectMatchMemberDialog;
